import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import { Volume2, VolumeX, Settings, Play } from "lucide-react";

const speakSupported = () => typeof window !== "undefined" && "speechSynthesis" in window;

function pickVoice(voiceName, lang = "pt-BR") {
  if (!speakSupported()) return null;
  const voices = window.speechSynthesis.getVoices();
  if (voiceName) {
    const byName = voices.find((v) => v.name === voiceName);
    if (byName) return byName;
  }
  const langLower = (lang || "pt-BR").toLowerCase();
  return (
    voices.find((v) => v.lang && v.lang.toLowerCase() === langLower) ||
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("pt")) ||
    null
  );
}

function speakOnce(text, voiceName, lang) {
  if (!speakSupported()) return;
  try {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang || "pt-BR";
    const v = pickVoice(voiceName, lang);
    if (v) u.voice = v;
    u.rate = 1.0;
    u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (e) {}
}

function speakRepeat(text, { repeats = 1, gapMs = 500, voiceName, lang } = {}) {
  const n = Math.max(1, Math.min(3, parseInt(repeats, 10) || 1));
  const gap = Math.max(0, Math.min(3000, parseInt(gapMs, 10) || 0));
  speakOnce(text, voiceName, lang);
  for (let i = 1; i < n; i++) {
    setTimeout(() => speakOnce(text, voiceName, lang), i * (gap + 2000));
  }
}

export default function TVPanel() {
  const nav = useNavigate();
  const { tenantSlug } = useParams();
  const [state, setState] = useState({ categories: [], server_time: null });
  const [tenant, setTenant] = useState(null);
  const [started, setStarted] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [callQueue, setCallQueue] = useState([]);
  const [showingCall, setShowingCall] = useState(null);
  const [exitOpen, setExitOpen] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [callSettings, setCallSettings] = useState({
    announcement_repeats: 1,
    announcement_gap_ms: 500,
    voice_name: null,
    voice_language: "pt-BR",
    announcement_message_style: "full",
  });
  const seenCallsRef = useRef(new Set());
  const seenReplaysRef = useRef(new Map()); // call_id -> last_replay_at seen

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/calls");
      setCallSettings((s) => ({ ...s, ...data }));
    } catch (e) {}
    try {
      const { data } = await api.get("/tenant/me");
      setTenant(data);
    } catch (e) {}
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      data.categories.forEach((c) => {
        const pc = c.pending_call;
        if (!pc) return;
        const prof = (c.queue || []).find((p) => p.id === pc.professional_id);
        if (!prof) return;
        if (!seenCallsRef.current.has(pc.id)) {
          seenCallsRef.current.add(pc.id);
          setCallQueue((q) => [...q, {
            id: pc.id,
            name: prof.name,
            category: c.name,
            category_id: c.id,
            trigger: `call:${pc.id}`,
          }]);
        }
        // Replay tick: enqueue a new "announce" for the same call
        const last = pc.last_replay_at;
        if (last && seenReplaysRef.current.get(pc.id) !== last) {
          seenReplaysRef.current.set(pc.id, last);
          setCallQueue((q) => [...q, {
            id: pc.id,
            name: prof.name,
            category: c.name,
            category_id: c.id,
            trigger: `replay:${last}`,
          }]);
        }
      });
      setState(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!started) return undefined;
    loadSettings();
    load();
    const t = setInterval(load, 3000);
    const ts = setInterval(loadSettings, 30000);
    return () => { clearInterval(t); clearInterval(ts); };
  }, [load, loadSettings, started]);

  // Advance to next call from queue when nothing is being shown
  useEffect(() => {
    if (showingCall || callQueue.length === 0) return;
    const next = callQueue[0];
    setShowingCall(next);
    setCallQueue((q) => q.slice(1));
    if (voiceOn) {
      const style = callSettings.announcement_message_style || "full";
      const text = style === "name_only"
        ? `${next.name}.`
        : style === "short"
        ? `${next.name}, cliente aguardando.`
        : `${next.name}, cliente aguardando atendimento.`;
      speakRepeat(text, {
        repeats: callSettings.announcement_repeats,
        gapMs: callSettings.announcement_gap_ms,
        voiceName: callSettings.voice_name,
        lang: callSettings.voice_language,
      });
    }
  }, [callQueue, showingCall, voiceOn, callSettings]);

  // Auto-close overlay after ~10s (independent of callQueue changes)
  useEffect(() => {
    if (!showingCall) return undefined;
    const t = setTimeout(() => setShowingCall(null), 10000);
    return () => clearTimeout(t);
  }, [showingCall]);

  // Early-close when the shown call is no longer pending (accepted / declined / timeout / cancelled)
  useEffect(() => {
    if (!showingCall) return;
    const stillPending = (state.categories || []).some(
      (c) => c.pending_call && c.pending_call.id === showingCall.id,
    );
    if (!stillPending) {
      setShowingCall(null);
    }
  }, [state, showingCall]);

  const startTv = () => {
    // unlock audio context via user gesture
    if (speakSupported()) {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
    setStarted(true);
  };

  const tryExit = async () => {
    if (pinInput.length !== 4) {
      toast.error("Digite o PIN de 4 dígitos");
      return;
    }
    try {
      const { data } = await api.post("/settings/tv-pin/verify", { pin: pinInput });
      if (data.ok) {
        setExitOpen(false);
        setPinInput("");
        nav(`/${tenantSlug}/fila`);
      } else {
        toast.error("PIN incorreto");
        setPinInput("");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!started) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black text-white">
        <button
          data-testid="btn-start-tv"
          onClick={startTv}
          className="rounded-full bg-white px-10 py-6 text-2xl font-bold uppercase tracking-widest text-black hover:scale-105 transition-transform inline-flex items-center gap-3"
        >
          <Play size={28} /> Iniciar Painel TV
        </button>
      </div>
    );
  }

  const activeCats = state.categories.filter((c) => {
    if (!c.active) return false;
    if (!c.hide_when_empty) return true;
    const hasQueue = (c.queue || []).length > 0;
    const hasWorking = (state.professionals || []).some(
      (p) => (p.category_ids || []).includes(c.id) && p.status && p.status !== "off_duty",
    );
    return hasQueue || hasWorking;
  });

  return (
    <div className="fixed inset-0 z-[9998] bg-black text-white overflow-hidden">
      {/* Header discreto */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={tenant?.logo_url || "/logo.png"} alt={tenant?.tv_display_name || "FILA Gestão"} className="h-10 w-10 object-contain rounded-full" onError={(e) => { e.currentTarget.src = "/logo.png"; }} />
          <div className="font-display text-2xl font-semibold tracking-tight">{tenant?.tv_display_name || tenant?.display_name || "FILA Gestão"}</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="btn-toggle-voice"
            onClick={() => setVoiceOn((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
          >
            {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
            Voz: {voiceOn ? "ATIVADA" : "DESATIVADA"}
          </button>
          <button
            data-testid="btn-tv-exit"
            onClick={() => setExitOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-2 text-xs text-white/70 hover:bg-white/10"
          >
            <Settings size={14} /> Sair do Painel TV
          </button>
        </div>
      </div>

      {/* Grid de categorias */}
      <div className="p-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 h-[calc(100vh-88px)] overflow-y-auto">
        {activeCats.length === 0 && (
          <div className="col-span-full flex items-center justify-center text-3xl text-white/40">
            Nenhuma categoria ativa
          </div>
        )}
        {activeCats.map((cat) => (
          <div
            key={cat.id}
            data-testid={`tv-cat-${cat.name.toLowerCase()}`}
            className="rounded-3xl border-2 border-white/10 bg-white/5 p-6"
          >
            <div className="mb-5 flex items-baseline justify-between">
              <div className="font-display text-4xl font-bold uppercase tracking-tight">{cat.name}</div>
              <div className="text-sm uppercase tracking-widest text-white/50">
                {(cat.queue_mode || "vez") === "quantidade" ? "Por quantidade" : "Por vez"}
              </div>
            </div>
            {cat.queue.length === 0 ? (
              <div className="py-10 text-center text-2xl text-white/30">Nenhuma profissional</div>
            ) : (
              <ul className="space-y-3">
                {cat.queue.map((p, idx) => {
                  const isNext = p.id === cat.na_vez_id;
                  return (
                    <li
                      key={p.id}
                      className={`flex items-center gap-4 rounded-2xl p-4 ${
                        isNext ? "bg-pink-500 text-black shadow-lg shadow-pink-500/30" : "bg-white/5"
                      }`}
                    >
                      <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-2xl font-bold ${isNext ? "bg-black text-pink-300" : "bg-white/10 text-white/70"}`}>
                        {isNext ? "🥇" : idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-3xl font-bold">{p.name}</div>
                        {isNext && <div className="text-sm font-bold uppercase tracking-[0.3em]">NA VEZ</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Overlay de chamada */}
      {showingCall && (
        <div
          data-testid="tv-call-overlay"
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-black text-white animate-pulse-slow"
          style={{ background: "linear-gradient(135deg,#000,#4b0021)" }}
        >
          <div data-testid="tv-call-title" className="text-5xl md:text-6xl font-bold text-center px-6">🔔 CLIENTE AGUARDANDO ATENDIMENTO</div>
          <div className="mt-8 font-display text-[8rem] leading-none font-black uppercase tracking-tight text-pink-400 text-center px-6">
            {showingCall.name}
          </div>
          <div className="mt-6 text-4xl uppercase tracking-[0.4em] text-white/70">{showingCall.category}</div>
        </div>
      )}

      {/* Sair dialog */}
      {exitOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-black">
            <div className="font-display text-2xl font-semibold">Sair do Painel TV</div>
            <div className="mt-1 text-sm text-gray-500">Informe o PIN administrativo (4 dígitos).</div>
            <input
              data-testid="input-tv-pin"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ""))}
              className="mt-4 w-full rounded-lg border border-gray-200 px-4 py-3 text-center text-2xl tracking-[0.5em] outline-none focus:border-black"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setExitOpen(false); setPinInput(""); }} className="rounded-full border border-gray-200 px-4 py-2 text-sm">Cancelar</button>
              <button data-testid="btn-tv-exit-confirm" onClick={tryExit} className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white">Sair</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
